/**
 * PFT Partner Product — Cascading Filter Engine
 * Cotton Bureau-style inline product browsing on partner pages.
 *
 * Architecture:
 * - Liquid renders lookups JSON (designs + colours) and seed products JSON (first 250)
 * - JS parses seed, renders initial state instantly
 * - Background-fetches remaining products if collection > 250
 * - All selector changes are pure client-side filtering (< 1ms)
 * - 7 cascading selectors: Artwork → Garment → Cut → Style → Weight → Colour → Size
 *
 * Vanilla JS, no frameworks, delegated event listeners on document.
 */
(function () {
  'use strict';

  /* =========================================================================
     1. CONSTANTS & STATE
     ========================================================================= */

  var SELECTOR_ORDER = ['artwork', 'garment', 'cut', 'style', 'weight', 'colour', 'size'];
  var TAG_PREFIXES = {
    garment: 'garment-',
    cut: 'cut-',
    style: 'style-',
    weight: 'weight-',
    colour: 'colour-'
  };

  // Icons for garment/style selectors (inline SVG strings)
  var GARMENT_ICONS = {
    't-shirt': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 8 L5 10 L5 22 L19 22 L19 10 L23 8 L20 4 L16 2"/><path d="M8 2 C8 2 9 6 12 6 C15 6 16 2 16 2"/></svg>',
    'jumper': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C8 2 9 5 12 5 C15 5 16 2 16 2"/><line x1="6" y1="18" x2="18" y2="18"/></svg>',
    'hoodie': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C8 2 9 5 12 5 C15 5 16 2 16 2"/><path d="M10 2 C10 2 8 4 9 7 L12 10 L15 7 C16 4 14 2 14 2" stroke-width="1"/></svg>',
    'tote-bag': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="14" rx="1"/><path d="M8 8 L8 5 C8 3 10 2 12 2 C14 2 16 3 16 5 L16 8"/></svg>',
    'onesie': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L5 4 L3 7 L6 9 L6 16 L8 22 L11 22 L11 16 L13 16 L13 22 L16 22 L18 16 L18 9 L21 7 L19 4 L16 2"/><path d="M8 2 C8 2 9 5 12 5 C15 5 16 2 16 2"/></svg>',
    'bib': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3 C7 3 9 2 12 2 C15 2 17 3 17 3 L19 8 L17 8 L17 20 C17 21 15 22 12 22 C9 22 7 21 7 20 L7 8 L5 8 Z"/><circle cx="12" cy="7" r="2"/></svg>',
    // 'other' is a category (tote/bib/onesie/shopping-bag). Use a bag, NOT the
    // t-shirt fallback that icons[Object.keys(icons)[0]] used to render.
    'other': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="14" rx="1"/><path d="M8 8 L8 5 C8 3 10 2 12 2 C14 2 16 3 16 5 L16 8"/></svg>'
  };

  var GARMENT_SORT_ORDER = { 't-shirt': 0, 'jumper': 1, 'hoodie': 2, 'tote-bag': 3, 'onesie': 4, 'bib': 5 };

  var CUT_SORT_ORDER = { 'adult': 0, 'kids': 1, 'baby': 2 };

  // Style order within each garment. 0 = leftmost. Unknown values fall back to alpha.
  var STYLE_SORT_ORDER = {
    'short-sleeve': 0, 'long-sleeve': 1,
    'crewneck-short-sleeve': 0, 'ladies-short-sleeve': 1, 'crewneck-long-sleeve': 2,
    'cosy': 0, 'zip': 1,
    'crewneck': 0, 'regular': 0,
    'shopping-bag': 0, 'tote-bag': 1,
    'bib': 0, 'onesie': 1
  };

  var STYLE_ICONS = {
    'short-sleeve': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L2 8 L6 9 L6 22 L18 22 L18 9 L22 8 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/></svg>',
    'long-sleeve': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/></svg>',
    // New T-Shirt style slugs (post-catalog refresh) reuse existing icons
    'crewneck-short-sleeve': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L2 8 L6 9 L6 22 L18 22 L18 9 L22 8 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/></svg>',
    'ladies-short-sleeve': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L2 8 L6 9 L6 22 L18 22 L18 9 L22 8 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/></svg>',
    'crewneck-long-sleeve': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/></svg>',
    // crewneck mirrors the Jumper garment icon
    'crewneck': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C8 2 9 5 12 5 C15 5 16 2 16 2"/><line x1="6" y1="18" x2="18" y2="18"/></svg>',
    // cosy mirrors the Hoodie garment icon
    'cosy': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C8 2 9 5 12 5 C15 5 16 2 16 2"/><path d="M10 2 C10 2 8 4 9 7 L12 10 L15 7 C16 4 14 2 14 2" stroke-width="1"/></svg>',
    'zip': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L1 14 L5 14 L6 10 L6 22 L18 22 L18 10 L19 14 L23 14 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/><line x1="12" y1="5" x2="12" y2="22"/></svg>',
    'regular': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="4" y1="8" x2="20" y2="8"/></svg>',
    // Shopping bag: paper-shopper silhouette with two arched handles
    'shopping-bag': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8 L4 22 L20 22 L19 8 Z"/><path d="M9 8 V6 C9 4.5 10 3 12 3 C14 3 15 4.5 15 6 V8"/></svg>',
    // Tote bag: trapezoidal body with two straight upright straps
    'tote-bag': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8 L5 22 L19 22 L20 8 Z"/><path d="M8 8 V3 H10 V8"/><path d="M14 8 V3 H16 V8"/></svg>',
    // Bib: curved shoulders, Velcro neck tab, rounded body
    'bib': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3 C9 3 9 5 7 6 C5 7 4 8 4 10 L6 11 L7 20 C7 21 9 22 12 22 C15 22 17 21 17 20 L18 11 L20 10 C20 8 19 7 17 6 C15 5 15 3 14 3 Z"/><circle cx="12" cy="5.5" r="1"/></svg>',
    // Onesie: torso with short legs and crotch snaps
    'onesie': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2 L4 4 L2 8 L6 9 L6 16 L7 22 L11 22 L11 18 L13 18 L13 22 L17 22 L18 16 L18 9 L22 8 L20 4 L16 2"/><path d="M8 2 C9 5 15 5 16 2"/><circle cx="10.5" cy="19" r="0.4" fill="currentColor"/><circle cx="13.5" cy="19" r="0.4" fill="currentColor"/></svg>'
  };

  var state = {
    allProducts: [],
    lookups: { designs: {}, colours: {} },
    selected: {},    // { artwork: 'pft-logo-dark', garment: 't-shirt', ... }
    currentProduct: null,
    currentVariant: null,
    currentImageIndex: 0,
    fetchComplete: false,
    sectionEl: null,
    collectionHandle: '',
    totalProducts: 0,
    userSizeByFamily: { adult: null, kids: null, baby: null }  // Sticky size per size-family once the user explicitly picks one
  };

  /* Artwork carousel state */
  var artworkItemCount = 0;

  /* Global touch tracking for artwork tap detection */
  var touchStartTime = 0;
  var touchStartX = 0;
  var touchStartY = 0;
  var touchWasTap = false;
  document.addEventListener('touchstart', function (e) {
    touchStartTime = Date.now();
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchWasTap = true;
  }, { passive: true, capture: true });
  document.addEventListener('touchmove', function (e) {
    if (touchWasTap) {
      var dx = e.touches[0].clientX - touchStartX;
      var dy = e.touches[0].clientY - touchStartY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        touchWasTap = false;
      }
    }
  }, { passive: true, capture: true });

  /* =========================================================================
     2. INIT
     ========================================================================= */

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    state.sectionEl = document.querySelector('.pf-partner-product');
    if (!state.sectionEl) return;

    state.collectionHandle = state.sectionEl.dataset.collectionHandle || '';
    state.totalProducts = parseInt(state.sectionEl.dataset.totalProducts, 10) || 0;

    // Parse lookups
    var lookupsEl = document.getElementById('pf-lookups-data');
    if (lookupsEl) {
      try { state.lookups = JSON.parse(lookupsEl.textContent); } catch (e) { console.error('[PFT] Lookups parse error:', e); }
    }

    // Parse seed products
    var seedEl = document.getElementById('pf-seed-data');
    if (seedEl) {
      try {
        var raw = JSON.parse(seedEl.textContent);
        state.allProducts = raw.map(normalizeProduct);
      } catch (e) { console.error('[PFT] Seed parse error:', e); }
    }

    if (state.allProducts.length === 0) {
      // bug-299 (2026-08-04): the Liquid empty-state now catches 0-product
      // collections, but if we still get here (e.g. seed parse error), replace the
      // permanent shimmer skeleton with a visible message instead of bailing silently.
      if (state.sectionEl) {
        var shimmers = state.sectionEl.querySelectorAll('.pf-shimmer');
        for (var si = 0; si < shimmers.length; si++) shimmers[si].style.display = 'none';
        var wrap = state.sectionEl.querySelector('.pf-pp__image-wrap');
        if (wrap) {
          var msg = document.createElement('p');
          msg.className = 'pf-pp__empty-msg';
          msg.textContent = 'No products are available here yet — check back soon!';
          wrap.appendChild(msg);
        }
      }
      return;
    }

    // Check for deep link
    var params = new URLSearchParams(window.location.search);
    var deepLinkHandle = params.get('product');

    var deepLinked = null;
    if (deepLinkHandle) {
      deepLinked = state.allProducts.find(function (p) { return p.handle === deepLinkHandle; });
      if (!deepLinked) {
        // Beyond the 250-product seed (e.g. arriving from a /products/<handle> link):
        // fetch it directly and only THEN render, so the URL and selection never
        // flip to the default product first (fix N7).
        fetchProductByHandle(deepLinkHandle).then(function (p) {
          if (p) state.allProducts.push(p);
          finishInit(p);
        });
        return;
      }
    }

    // The partner's featured product can also sit beyond the seed (Vegan Society has
    // ~700 products): fetch it the same way, or the page silently opens on another design.
    var featuredHandle = (state.sectionEl && state.sectionEl.dataset.featuredProductHandle) || '';
    if (!deepLinkHandle && featuredHandle &&
        !state.allProducts.some(function (p) { return p.handle === featuredHandle; })) {
      fetchProductByHandle(featuredHandle).then(function (p) {
        if (p) state.allProducts.push(p);
        finishInit(null);
      });
      return;
    }
    finishInit(deepLinked);
  }

  /** Fetch one product via the AJAX API; null unless it belongs to THIS partner. */
  function fetchProductByHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (p) {
        if (!p || !Array.isArray(p.tags)) return null;
        if (state.collectionHandle && p.tags.indexOf('partner-' + state.collectionHandle) === -1) return null;
        var images = (p.images || []).filter(Boolean);
        var detail = images.filter(function (src) { return src.toLowerCase().indexOf('detail') !== -1; })[0] || '';
        return normalizeProduct({
          id: p.id,
          handle: p.handle,
          url: '/products/' + p.handle,
          title: p.title,
          tags: p.tags,
          featuredImage: p.featured_image || images[0] || '',
          images: images,
          detailImage: detail,
          price: p.price,
          compareAtPrice: p.compare_at_price,
          available: p.available,
          variants: (p.variants || []).map(function (v) {
            return { id: v.id, title: v.title, price: v.price, available: v.available };
          })
        });
      })
      .catch(function (err) {
        console.warn('[PFT] Deep-link product fetch failed:', err);
        return null;
      });
  }

  function finishInit(deepLinked) {
    // Partner-configured featured product (metafield → section data attribute)
    var featuredHandle = (state.sectionEl && state.sectionEl.dataset.featuredProductHandle) || '';

    // Set initial product — priority: deep link > partner-featured > design-hero > first
    var initialProduct = deepLinked || null;
    if (!initialProduct && featuredHandle) {
      initialProduct = state.allProducts.find(function (p) { return p.handle === featuredHandle; });
    }
    if (!initialProduct) {
      initialProduct = state.allProducts.find(function (p) { return p.tags.indexOf('design-hero') !== -1; }) || state.allProducts[0];
    }

    // Parse size charts data
    initSizeCharts();

    setSelectionsFromProduct(initialProduct);
    renderAllSelectors();
    productSwap(initialProduct);

    // Background fetch remaining products
    if (state.totalProducts > 250 && state.collectionHandle) {
      backgroundFetch();
    } else {
      state.fetchComplete = true;
    }

    // Event delegation
    bindEvents();
  }

  /* =========================================================================
     3. DATA NORMALIZATION
     ========================================================================= */

  function normalizeProduct(p) {
    var dims = parseTagDimensions(p.tags);
    return {
      id: p.id,
      handle: p.handle,
      url: p.url || '/products/' + p.handle,
      title: p.title || '',
      designName: p.designName || '',
      tags: Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(', ') : []),
      featuredImage: p.featuredImage || p.featured_image || '',
      images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
      detailImage: p.detailImage || '',
      price: normalizePrice(p.price),
      compareAtPrice: p.compareAtPrice || p.compare_at_price ? normalizePrice(p.compareAtPrice || p.compare_at_price) : null,
      available: p.available !== false,
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id,
          title: v.title,
          price: normalizePrice(v.price),
          available: v.available !== false
        };
      }),
      dims: dims
    };
  }

  /** Normalize price to cents (integer). Handles "2500" (Liquid) and "25.00" (AJAX). */
  function normalizePrice(val) {
    if (val == null) return 0;
    var n = typeof val === 'string' ? parseFloat(val) : val;
    // If it looks like dollars (has decimal and < 1000), convert to cents
    if (n > 0 && n < 1000 && String(val).indexOf('.') !== -1) {
      return Math.round(n * 100);
    }
    return Math.round(n);
  }

  /** Parse tag array into structured dimensions object. */
  function parseTagDimensions(tags) {
    var arr = Array.isArray(tags) ? tags : (typeof tags === 'string' ? tags.split(', ') : []);
    var dims = { artwork: '', garment: '', cut: '', style: '', weight: '', colour: '' };

    for (var i = 0; i < arr.length; i++) {
      var tag = arr[i].trim().toLowerCase();
      if (tag.indexOf('garment-') === 0) dims.garment = tag.slice(8);
      else if (tag.indexOf('cut-') === 0) dims.cut = tag.slice(4);
      else if (tag.indexOf('style-') === 0) dims.style = tag.slice(6);
      else if (tag.indexOf('weight-') === 0) dims.weight = tag.slice(7);
      else if (tag.indexOf('colour-') === 0) dims.colour = tag.slice(7);
      else if (tag.indexOf('partner-') === 0) { /* skip */ }
      else if (tag === 'design-hero') { /* skip */ }
      else if (tag.indexOf('-') !== -1 && !dims.artwork) {
        // Potential design handle — check lookups
        if (state.lookups.designs && state.lookups.designs[tag]) {
          dims.artwork = tag;
        }
      }
    }

    // Second pass for design handle if not found in first pass
    if (!dims.artwork) {
      for (var j = 0; j < arr.length; j++) {
        var t = arr[j].trim().toLowerCase();
        if (t.indexOf('garment-') === 0 || t.indexOf('cut-') === 0 || t.indexOf('style-') === 0 ||
            t.indexOf('weight-') === 0 || t.indexOf('colour-') === 0 || t.indexOf('partner-') === 0 ||
            t === 'design-hero') continue;
        // Remaining tag that isn't a known prefix is the design handle
        dims.artwork = t;
        break;
      }
    }

    return dims;
  }

  function setSelectionsFromProduct(product) {
    state.selected = {
      artwork: product.dims.artwork,
      garment: product.dims.garment,
      cut: product.dims.cut,
      style: product.dims.style,
      weight: product.dims.weight,
      colour: product.dims.colour,
      size: resolveInitialSize(product)
    };
  }

  function getSizeFamily(product) {
    if (!product) return 'adult';
    var cut = (product.dims && product.dims.cut) || '';
    if (cut === 'baby') return 'baby';
    if (cut === 'kids') return 'kids';
    return 'adult';
  }

  function getDefaultSizeIndex(variantCount) {
    if (variantCount <= 0) return 0;
    return Math.floor((variantCount - 1) / 2);
  }

  function resolveInitialSize(product) {
    if (!product || !product.variants || product.variants.length === 0) return '';
    var family = getSizeFamily(product);
    var sticky = state.userSizeByFamily && state.userSizeByFamily[family];
    if (sticky) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].title.toLowerCase() === sticky) {
          return sticky;
        }
      }
    }
    var idx = getDefaultSizeIndex(product.variants.length);
    return product.variants[idx].title.toLowerCase();
  }

  /* =========================================================================
     4. BACKGROUND FETCH
     ========================================================================= */

  function backgroundFetch() {
    var totalPages = Math.ceil(state.totalProducts / 250);
    var fetched = 0;

    // Pages 2 through N (page 1 already came from Liquid seed)
    for (var page = 2; page <= totalPages; page++) {
      fetchPage(page);
    }

    function fetchPage(pageNum) {
      var url = '/collections/' + state.collectionHandle + '/products.json?limit=250&page=' + pageNum;
      fetch(url)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.products && data.products.length > 0) {
            var existingIds = new Set(state.allProducts.map(function (p) { return p.id; }));
            var newProducts = data.products
              .filter(function (p) { return !existingIds.has(p.id); })
              .map(function (p) {
                // Find detail image from product images
                var bgDetailImg = '';
                if (p.images) {
                  for (var di = 0; di < p.images.length; di++) {
                    if (p.images[di].src && p.images[di].src.toLowerCase().indexOf('detail') !== -1) {
                      bgDetailImg = p.images[di].src;
                      break;
                    }
                  }
                }
                return normalizeProduct({
                  id: p.id,
                  handle: p.handle,
                  url: '/products/' + p.handle,
                  title: p.title,
                  tags: p.tags,
                  featuredImage: p.images && p.images.length > 0 ? p.images[0].src : '',
                  images: (p.images || []).map(function (im) { return im.src; }).filter(Boolean),
                  detailImage: bgDetailImg,
                  price: p.variants && p.variants[0] ? p.variants[0].price : '0',
                  compareAtPrice: p.variants && p.variants[0] ? p.variants[0].compare_at_price : null,
                  available: p.available,
                  variants: (p.variants || []).map(function (v) {
                    return { id: v.id, title: v.title, price: v.price, available: v.available };
                  })
                });
              });

            // Enrich lookups with designs discovered beyond the first 250 products
            for (var n = 0; n < newProducts.length; n++) {
              var np = newProducts[n];
              if (np.dims.artwork && !state.lookups.designs[np.dims.artwork]) {
                var designName = np.designName || np.title.split(' | ')[0] || np.dims.artwork;
                state.lookups.designs[np.dims.artwork] = {
                  name: designName,
                  artworkUrl: np.featuredImage || ''
                };
              }
            }

            // Merge into pool
            state.allProducts = state.allProducts.concat(newProducts);
          }
        })
        .catch(function (err) {
          console.warn('[PFT] Background fetch page ' + pageNum + ' failed:', err);
        })
        .then(pageDone);
    }

    function pageDone() {
      fetched++;
      if (fetched < totalPages - 1) return;
      state.fetchComplete = true;
      // Re-render current selectors with full data
      cascadeFrom(0);
      // cascadeFrom only rebuilds the colour carousel when the product changes. A
      // product whose other colours sit beyond the 250-product seed (e.g. opened
      // from a ?product= link) would otherwise keep showing just its own colour.
      if (state.currentProduct) {
        renderVariantCarousel(state.currentProduct);
        updateCarouselArrows();
      }
    }
  }

  /* =========================================================================
     5. SELECTOR RENDERING
     ========================================================================= */

  function renderAllSelectors() {
    for (var i = 0; i < SELECTOR_ORDER.length; i++) {
      renderSelector(i);
    }
  }

  function renderSelector(index) {
    var dimension = SELECTOR_ORDER[index];
    var fieldset = state.sectionEl.querySelector('[data-selector="' + dimension + '"]');
    if (!fieldset) return;

    var track = fieldset.querySelector('[data-track]');
    if (!track) return;

    // Get available values for this dimension given selections above
    var pool = filterPool(index);
    var values;
    var html = '';

    if (dimension === 'size') {
      // Size uses current product's variants
      var product = state.currentProduct;
      if (!product) return;
      values = product.variants.map(function (v) {
        return { value: v.title.toLowerCase(), label: v.title, available: v.available };
      });
      for (var s = 0; s < values.length; s++) {
        var sv = values[s];
        var isSelected = state.selected.size === sv.value;
        html += '<button class="pf-selector__option" role="radio" aria-checked="' + isSelected + '"' +
          ' data-selector="size" data-value="' + escapeAttr(sv.value) + '"' +
          (sv.available ? '' : ' data-unavailable="true"') + '>' +
          '<span class="pf-selector__option-label">' + escapeHtml(sv.label) + '</span></button>';
      }
    } else if (dimension === 'artwork') {
      values = getUniqueValues(pool, 'artwork');
      for (var a = 0; a < values.length; a++) {
        var av = values[a];
        var designInfo = state.lookups.designs[av.value] || {};
        var imgUrl = designInfo.artworkUrl || '';
        var isSelected = state.selected.artwork === av.value;
        html += '<button class="pf-selector__option" role="radio" aria-checked="' + isSelected + '"' +
          ' data-selector="artwork" data-value="' + escapeAttr(av.value) + '">' +
          (imgUrl ? '<img src="' + escapeAttr(imgUrl) + '" alt="' + escapeAttr(designInfo.name || av.label) + '" loading="lazy">' :
          '<span class="pf-selector__option-label">' + escapeHtml(av.label) + '</span>') +
          '</button>';
      }
    } else if (dimension === 'colour') {
      values = getUniqueValues(pool, 'colour');
      for (var c = 0; c < values.length; c++) {
        var cv = values[c];
        var colourInfo = state.lookups.colours[cv.value] || {};
        var hex = colourInfo.hex || '#ccc';
        var colourName = colourInfo.name || cv.label;
        var isSelected = state.selected.colour === cv.value;
        var isAvail = cv.available;
        var thumbImg = colourInfo.thumbnail || '';
        html += '<button class="pf-selector__option" role="radio" aria-checked="' + isSelected + '"' +
          ' data-selector="colour" data-value="' + escapeAttr(cv.value) + '"' +
          ' title="' + escapeAttr(colourName) + '"' +
          (isAvail ? '' : ' data-unavailable="true"') + '>' +
          (thumbImg ? '<img src="' + escapeAttr(thumbImg) + '" alt="' + escapeAttr(colourName) + '" loading="lazy">' :
          '<span class="pf-selector__colour-dot" style="background-color:' + escapeAttr(hex) + '"></span>') +
          '</button>';
      }
    } else if (dimension === 'garment' || dimension === 'style') {
      values = getUniqueValues(pool, dimension);
      var icons = dimension === 'garment' ? GARMENT_ICONS : STYLE_ICONS;
      for (var g = 0; g < values.length; g++) {
        var gv = values[g];
        var icon = icons[gv.value] || icons[Object.keys(icons)[0]] || '';
        var isSelected = state.selected[dimension] === gv.value;
        var isAvail = gv.available;
        html += '<button class="pf-selector__option" role="radio" aria-checked="' + isSelected + '"' +
          ' data-selector="' + dimension + '" data-value="' + escapeAttr(gv.value) + '"' +
          (isAvail ? '' : ' data-unavailable="true"') + '>' +
          (icon ? '<span class="pf-selector__option-icon">' + icon + '</span>' : '') +
          '<span class="pf-selector__option-label">' + escapeHtml(gv.label) + '</span></button>';
      }
    } else {
      // cut, weight — text-only
      values = getUniqueValues(pool, dimension);
      for (var t = 0; t < values.length; t++) {
        var tv = values[t];
        var isSelected = state.selected[dimension] === tv.value;
        var isAvail = tv.available;
        html += '<button class="pf-selector__option" role="radio" aria-checked="' + isSelected + '"' +
          ' data-selector="' + dimension + '" data-value="' + escapeAttr(tv.value) + '"' +
          (isAvail ? '' : ' data-unavailable="true"') + '>' +
          '<span class="pf-selector__option-label">' + escapeHtml(tv.label) + '</span></button>';
      }
    }

    track.innerHTML = html;
    fieldset.setAttribute('data-ready', 'true');

    // "CHOOSE PRINT" only makes sense when the partner has 2+ designs to pick
    // from. With a single artwork there is nothing to choose, so hide the whole
    // carousel rather than show a lone, non-functional tile.
    if (dimension === 'artwork' && fieldset.parentNode) {
      fieldset.parentNode.hidden = values.length <= 1;
    }

    // Init carousel arrows
    initCarouselArrows(fieldset);
  }

  /** Filter product pool based on all selections ABOVE the given selector index. */
  function filterPool(index) {
    var pool = state.allProducts;
    for (var i = 0; i < index; i++) {
      var dim = SELECTOR_ORDER[i];
      if (dim === 'size') continue;
      var val = state.selected[dim];
      if (!val) continue;
      pool = pool.filter(function (p) { return p.dims[dim] === val; });
    }
    return pool;
  }

  /** Get unique dimension values from a product pool, with availability info. */
  function getUniqueValues(pool, dimension) {
    var seen = {};
    var result = [];

    // Also check availability by further filtering
    for (var i = 0; i < pool.length; i++) {
      var val = pool[i].dims[dimension];
      if (!val || seen[val]) continue;
      seen[val] = true;

      // Check if this value has any available products downstream
      var available = pool[i].available;

      result.push({
        value: val,
        label: dimension === 'weight' ? formatWeightLabel(val) : titleCase(val),
        available: available
      });
    }

    if (dimension === 'garment') {
      return result.sort(function (a, b) {
        var oa = GARMENT_SORT_ORDER[a.value] !== undefined ? GARMENT_SORT_ORDER[a.value] : 99;
        var ob = GARMENT_SORT_ORDER[b.value] !== undefined ? GARMENT_SORT_ORDER[b.value] : 99;
        return oa - ob;
      });
    }
    if (dimension === 'style') {
      return result.sort(function (a, b) {
        var oa = STYLE_SORT_ORDER[a.value] !== undefined ? STYLE_SORT_ORDER[a.value] : 99;
        var ob = STYLE_SORT_ORDER[b.value] !== undefined ? STYLE_SORT_ORDER[b.value] : 99;
        if (oa !== ob) return oa - ob;
        return a.label.localeCompare(b.label);
      });
    }
    if (dimension === 'cut') {
      return result.sort(function (a, b) {
        var oa = CUT_SORT_ORDER[a.value] !== undefined ? CUT_SORT_ORDER[a.value] : 99;
        var ob = CUT_SORT_ORDER[b.value] !== undefined ? CUT_SORT_ORDER[b.value] : 99;
        if (oa !== ob) return oa - ob;
        return a.label.localeCompare(b.label);
      });
    }
    if (dimension === 'artwork') {
      // The partner's "Order on shop" choice: design_lookups gives each design its
      // position (`order`, 1 = first). Designs without one follow, alphabetically.
      var designs = (state.lookups && state.lookups.designs) || {};
      var artOrder = function (v) {
        var o = designs[v] && Number(designs[v].order);
        return o > 0 ? o : 1e9;
      };
      return result.sort(function (a, b) {
        var oa = artOrder(a.value);
        var ob = artOrder(b.value);
        if (oa !== ob) return oa - ob;
        return a.label.localeCompare(b.label);
      });
    }
    return result.sort(function (a, b) { return a.label.localeCompare(b.label); });
  }

  /* =========================================================================
     6. CASCADING FILTER ENGINE
     ========================================================================= */

  function cascadeFrom(fromIndex) {
    for (var i = fromIndex; i < SELECTOR_ORDER.length; i++) {
      var dim = SELECTOR_ORDER[i];
      if (dim === 'size') continue; // Size handled separately after product swap

      var pool = filterPool(i);
      var values = getUniqueValues(pool, dim);

      // If current selection still available, keep it. Otherwise, pick first.
      var currentVal = state.selected[dim];
      var stillAvailable = values.some(function (v) { return v.value === currentVal; });

      if (!stillAvailable && values.length > 0) {
        state.selected[dim] = values[0].value;
      }

      renderSelector(i);
    }

    // Find matching product
    var matched = findMatchingProduct();
    if (matched && matched !== state.currentProduct) {
      productSwap(matched);
    } else if (matched) {
      // Same product, but re-render size selector
      renderSelector(SELECTOR_ORDER.indexOf('size'));
      updateSizeSelection();
    }
  }

  function findMatchingProduct() {
    var sel = state.selected;
    var candidates = state.allProducts.filter(function (p) {
      return p.dims.artwork === sel.artwork &&
        p.dims.garment === sel.garment &&
        p.dims.cut === sel.cut &&
        p.dims.style === sel.style &&
        p.dims.weight === sel.weight &&
        p.dims.colour === sel.colour;
    });

    // Prefer the canonical product so an accidental duplicate (stale -1/-2 copy)
    // never wins the colour→product match.
    if (candidates.length > 0) return pickCanonicalProduct(candidates);

    // Closest match: score by matching dimensions (top-down priority)
    var best = null;
    var bestScore = -1;
    for (var i = 0; i < state.allProducts.length; i++) {
      var p = state.allProducts[i];
      var score = 0;
      if (p.dims.artwork === sel.artwork) score += 64;
      if (p.dims.garment === sel.garment) score += 32;
      if (p.dims.cut === sel.cut) score += 16;
      if (p.dims.style === sel.style) score += 8;
      if (p.dims.weight === sel.weight) score += 4;
      if (p.dims.colour === sel.colour) score += 2;
      if (score > bestScore) { bestScore = score; best = p; }
    }
    return best;
  }

  /* =========================================================================
     7. PRODUCT SWAP
     ========================================================================= */

  function productSwap(product) {
    state.currentProduct = product;
    state.currentImageIndex = 0;

    // Decide the initial main-image URL: first per-colour image if available,
    // else fall back to product.featuredImage (legacy single-image products).
    var initialImageUrl = (product.images && product.images.length > 0)
      ? product.images[0]
      : product.featuredImage;

    // Update main image
    var imgEl = document.getElementById('pf-pp-main-image');
    if (imgEl && initialImageUrl) {
      setMainImageUrl(initialImageUrl, product.title);
      imgEl.style.display = 'block';
      var skeleton = imgEl.parentElement.querySelector('.pf-pp__image-skeleton');
      if (skeleton) skeleton.style.display = 'none';
    }

    // Update colour name
    var colourNameEl = document.getElementById('pf-pp-colour-name');
    if (colourNameEl) {
      var colourInfo = state.lookups.colours[product.dims.colour];
      colourNameEl.textContent = colourInfo ? colourInfo.name : titleCase(product.dims.colour);
    }

    // Per-product description (item 3): show the box only when this product has one.
    renderDescription(product);

    // Render the new per-colour image carousel
    renderImageCarousel(product);

    // Update variant carousel (colour variants for current garment+cut+style+weight)
    renderVariantCarousel(product);

    // Update product image arrows + variant carousel arrows
    updateImageArrows();
    updateCarouselArrows();

    // Render size selector
    renderSelector(SELECTOR_ORDER.indexOf('size'));
    updateSizeSelection();

    // Update price
    updatePriceDisplay();

    // Update size guide
    renderSizeGuide();

    // Centre artwork carousel (in case product swap changed artwork)
    centreArtworkCarousel();

    // Update URL (querystring only)
    var url = new URL(window.location);
    url.searchParams.set('product', product.handle);
    history.replaceState(null, '', url.toString());
  }

  function updateSizeSelection() {
    var product = state.currentProduct;
    if (!product) return;

    // Find the selected size variant
    var selectedSize = state.selected.size;
    var variant = product.variants.find(function (v) { return v.title.toLowerCase() === selectedSize; });

    // If selected size doesn't exist on this product (size-family mismatch), resolve via family rule
    if (!variant) {
      var newSize = resolveInitialSize(product);
      if (newSize) {
        state.selected.size = newSize;
        variant = product.variants.find(function (v) { return v.title.toLowerCase() === newSize; });
        updateSelectorUI('size', newSize);
      }
    }

    state.currentVariant = variant;

    // Update hidden form
    var variantInput = document.getElementById('pf-pp-variant-id');
    if (variantInput && variant) {
      variantInput.value = variant.id;
    }

    // Enable/disable ATC + Checkout buttons
    var canBuy = variant && variant.available;
    var atcBtn = document.getElementById('pf-pp-atc-btn');
    var checkoutBtn = document.getElementById('pf-pp-checkout-btn');
    if (atcBtn) atcBtn.disabled = !canBuy;
    if (checkoutBtn) checkoutBtn.disabled = !canBuy;

    updatePriceDisplay();
  }

  function updatePriceDisplay() {
    var priceEl = document.getElementById('pf-pp-price');
    if (!priceEl) return;

    var variant = state.currentVariant;
    var product = state.currentProduct;
    if (!variant && product) {
      variant = product.variants[0];
    }
    if (!variant) { priceEl.innerHTML = '&nbsp;'; return; }

    var price = variant.price;
    var compare = product && product.compareAtPrice;

    if (compare && compare > price) {
      priceEl.innerHTML = '<span class="pf-pp__price-compare">' + formatMoney(compare) + '</span>' +
        '<span class="pf-pp__price-sale">' + formatMoney(price) + '</span>';
    } else {
      priceEl.textContent = formatMoney(price);
    }
  }

  /** Prices are already in the shopper's (presentment) currency — format them in it too,
   *  never a hardcoded euro sign (decision D1 / fix N8). */
  var moneyFormatter = null;
  function formatMoney(cents) {
    if (cents == null) return '';
    if (!moneyFormatter) {
      var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
      try {
        moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-IE', { style: 'currency', currency: currency });
      } catch (e) {
        moneyFormatter = { format: function (n) { return currency + ' ' + n.toFixed(2); } };
      }
    }
    return moneyFormatter.format(cents / 100);
  }

  /* =========================================================================
     8. VARIANT CAROUSEL (colour thumbnails below main image)
     ========================================================================= */

  /**
   * DUPLICATE-PRODUCT GUARD.
   * The catalog invariant is one product per (design × garment × cut × style ×
   * weight × colour). When a partner-portal publish accidentally creates the same
   * product twice, Shopify auto-suffixes the handle with -1 / -2. That surfaces on
   * the storefront as repeated, un-clickable colour swatches, and the wrong (stale)
   * copy can win the colour→product match. These helpers collapse a product list to
   * one product per colour, always preferring the canonical (un-suffixed) handle —
   * the original, which is also the copy the portal updates in place.
   */
  function hasDuplicateHandleSuffix(handle) {
    return /-\d+$/.test(handle || '');
  }

  /** From several products, pick the canonical one (un-suffixed handle wins). */
  function pickCanonicalProduct(products) {
    if (!products || products.length === 0) return null;
    if (products.length === 1) return products[0];
    return products.slice().sort(function (a, b) {
      var da = hasDuplicateHandleSuffix(a.handle) ? 1 : 0;
      var db = hasDuplicateHandleSuffix(b.handle) ? 1 : 0;
      if (da !== db) return da - db;                       // un-suffixed first
      if (a.handle.length !== b.handle.length) return a.handle.length - b.handle.length;
      return a.handle < b.handle ? -1 : 1;                 // stable tie-break
    })[0];
  }

  /** Collapse a product pool to one canonical product per colour. */
  function dedupeByColour(products) {
    var byColour = {};
    for (var i = 0; i < products.length; i++) {
      var c = products[i].dims.colour || '';
      if (!byColour[c]) byColour[c] = [];
      byColour[c].push(products[i]);
    }
    var result = [];
    for (var key in byColour) {
      if (byColour.hasOwnProperty(key)) result.push(pickCanonicalProduct(byColour[key]));
    }
    return result;
  }

  /** All products for the current garment+cut+style+weight+artwork, one per colour. */
  function colourVariantsFor(sel) {
    var pool = state.allProducts.filter(function (p) {
      return p.dims.artwork === sel.artwork &&
        p.dims.garment === sel.garment &&
        p.dims.cut === sel.cut &&
        p.dims.style === sel.style &&
        p.dims.weight === sel.weight;
    });
    return dedupeByColour(pool);
  }

  function renderVariantCarousel(currentProduct) {
    var track = document.getElementById('pf-pp-variant-track');
    if (!track) return;

    // Find all colour variants for current garment+cut+style+weight (deduped by colour)
    var sel = state.selected;
    var colourProducts = colourVariantsFor(sel);

    // Sort by colour name
    colourProducts.sort(function (a, b) {
      return (a.dims.colour || '').localeCompare(b.dims.colour || '');
    });

    var html = '';
    for (var i = 0; i < colourProducts.length; i++) {
      var cp = colourProducts[i];
      var isActive = cp.handle === currentProduct.handle;
      var imgUrl = cp.featuredImage;
      if (imgUrl) {
        imgUrl = imgUrl.replace(/&width=\d+/, '');
        imgUrl = imgUrl.indexOf('?') !== -1 ? imgUrl + '&width=120' : imgUrl + '?width=120';
      }
      html += '<button class="pf-pp__variant-thumb' + (isActive ? ' pf-pp__variant-thumb--active' : '') + '"' +
        ' data-variant-colour="' + escapeAttr(cp.dims.colour) + '"' +
        ' data-product-handle="' + escapeAttr(cp.handle) + '"' +
        ' aria-label="' + escapeAttr(titleCase(cp.dims.colour)) + '">' +
        (imgUrl ? '<img src="' + escapeAttr(imgUrl) + '" alt="' + escapeAttr(titleCase(cp.dims.colour)) + '" loading="lazy">' : '') +
        '</button>';
    }
    track.innerHTML = html;

    // Center the active thumbnail in the carousel
    centerActiveThumb(track);
  }

  /** Scroll the variant carousel so the active thumbnail is centred. */
  function centerActiveThumb(track) {
    setTimeout(function () {
      var activeThumb = track.querySelector('.pf-pp__variant-thumb--active');
      if (!activeThumb) return;
      // Use scrollLeft instead of scrollIntoView to avoid vertical page jump on mobile
      var thumbLeft = activeThumb.offsetLeft;
      var thumbWidth = activeThumb.offsetWidth;
      var trackWidth = track.offsetWidth;
      track.scrollLeft = thumbLeft - (trackWidth / 2) + (thumbWidth / 2);
    }, 0);
  }

  /* =========================================================================
     9. CAROUSEL CONTROLLER
     ========================================================================= */

  function initCarouselArrows(fieldset) {
    var track = fieldset.querySelector('[data-track]');
    var leftArrow = fieldset.querySelector('.pf-selector__arrow--left');
    var rightArrow = fieldset.querySelector('.pf-selector__arrow--right');
    if (!track || !leftArrow || !rightArrow) return;

    var dimension = fieldset.dataset.selector;

    // Artwork uses centre-focused infinite carousel
    if (dimension === 'artwork') {
      initArtworkCarousel(fieldset);
      return;
    }

    var items = track.children;
    var isColour = dimension === 'colour';
    var isMobile = window.innerWidth < 750;
    // Size now mirrors the Custom Print island: pills share the row equally
    // (flex:1 1 0) and the label wraps per-character (XXS -> X/X/S) when there are
    // many sizes, so all sizes fit on one row with NO arrows and even spacing.
    // Products whose sizes already fit comfortably are unaffected (they simply
    // don't get narrow enough to wrap). So we no longer switch size into the old
    // "4 visible + arrows" overflow mode.
    var isSizeMobileOverflow = false;
    var maxVisible = isColour ? 8 : 999;

    // Ensure the old mobile "4 visible" overflow layout is never applied to size.
    if (dimension === 'size') {
      fieldset.removeAttribute('data-overflow');
    }

    function updateArrows() {
      if (items.length <= maxVisible) {
        leftArrow.hidden = true;
        rightArrow.hidden = true;
        return;
      }
      // On mobile, colour uses infinite loop; on desktop, hide at edges
      if (isColour && isMobile) {
        leftArrow.hidden = false;
        rightArrow.hidden = false;
      } else {
        leftArrow.hidden = track.scrollLeft <= 2;
        rightArrow.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }
    }

    leftArrow.onclick = function () {
      if (isColour && isMobile) {
        loopColourScroll(-1, track, items);
      } else {
        var itemWidth = items[0] ? items[0].offsetWidth + 8 : 100;
        var scrollAmt = isColour ? itemWidth * 4 : (isSizeMobileOverflow ? itemWidth * 3 : itemWidth);
        track.scrollBy({ left: -scrollAmt, behavior: 'smooth' });
      }
    };

    rightArrow.onclick = function () {
      if (isColour && isMobile) {
        loopColourScroll(1, track, items);
      } else {
        var itemWidth = items[0] ? items[0].offsetWidth + 8 : 100;
        var scrollAmt = isColour ? itemWidth * 4 : (isSizeMobileOverflow ? itemWidth * 3 : itemWidth);
        track.scrollBy({ left: scrollAmt, behavior: 'smooth' });
      }
    };

    track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
    setTimeout(updateArrows, 300);

    // On mobile overflow, centre the currently-selected size in the visible 4-slot window
    if (isSizeMobileOverflow) {
      setTimeout(function () {
        var itemWidth = items[0] ? items[0].offsetWidth + 8 : 0;
        var selectedIdx = -1;
        for (var i = 0; i < items.length; i++) {
          if (items[i].getAttribute('aria-checked') === 'true') { selectedIdx = i; break; }
        }
        if (selectedIdx === -1) selectedIdx = Math.floor((items.length - 1) / 2);
        var scrollIdx = Math.max(0, Math.min(items.length - 4, selectedIdx - 1));
        track.scrollLeft = scrollIdx * itemWidth;
        updateArrows();
      }, 0);
    }
  }

  /** Continuous loop scroll for colour selector: move items to create infinite feel. */
  function loopColourScroll(direction, track, items) {
    if (items.length === 0) return;
    var itemWidth = items[0].offsetWidth + 8;
    var scrollAmount = itemWidth * 4; // scroll 4 items at a time

    if (direction > 0) {
      // Scroll right; if near end, move first items to end
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
        // At the end — move first batch of items to the back
        for (var i = 0; i < 4 && track.children.length > 4; i++) {
          track.appendChild(track.children[0]);
        }
        track.scrollLeft = 0;
      }
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    } else {
      // Scroll left; if at start, move last items to front
      if (track.scrollLeft <= 2) {
        for (var j = 0; j < 4 && track.children.length > 4; j++) {
          track.insertBefore(track.children[track.children.length - 1], track.children[0]);
        }
        track.scrollLeft = scrollAmount;
      }
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  }

  /* =========================================================================
     10. EVENT DELEGATION
     ========================================================================= */

  function bindEvents() {
    // Selector option clicks
    document.addEventListener('click', function (e) {
      // Selector option
      var optionBtn = e.target.closest('.pf-selector__option');
      if (optionBtn && state.sectionEl && state.sectionEl.contains(optionBtn)) {
        e.preventDefault();
        var dim = optionBtn.dataset.selector;
        var val = optionBtn.dataset.value;
        if (!dim || !val) return;
        if (optionBtn.getAttribute('data-unavailable') === 'true') return;

        // On touch devices, only allow artwork selection from a clean tap (no movement, < 300ms)
        if (dim === 'artwork' && 'ontouchstart' in window && !touchWasTap) {
          return;
        }

        if (dim === 'size') {
          state.selected.size = val;
          var fam = getSizeFamily(state.currentProduct);
          if (!state.userSizeByFamily) state.userSizeByFamily = { adult: null, kids: null, baby: null };
          state.userSizeByFamily[fam] = val;
          updateSelectorUI('size', val);
          updateSizeSelection();
        } else {
          state.selected[dim] = val;
          var dimIndex = SELECTOR_ORDER.indexOf(dim);
          if (dimIndex !== -1) {
            updateSelectorUI(dim, val);
            cascadeFrom(dimIndex + 1);
          }
        }
        return;
      }

      // Variant thumbnail click (colour swatch)
      var thumbBtn = e.target.closest('.pf-pp__variant-thumb');
      if (thumbBtn && state.sectionEl && state.sectionEl.contains(thumbBtn)) {
        e.preventDefault();
        var colourVal = thumbBtn.dataset.variantColour;
        if (colourVal) {
          state.selected.colour = colourVal;
          updateSelectorUI('colour', colourVal);
          cascadeFrom(SELECTOR_ORDER.indexOf('colour') + 1);
        }
        return;
      }

      // Image-thumbnail click (per-colour image carousel)
      var imageThumb = e.target.closest('.pf-pp__image-thumb');
      if (imageThumb && state.sectionEl && state.sectionEl.contains(imageThumb)) {
        e.preventDefault();
        var idx = parseInt(imageThumb.dataset.imageIndex, 10);
        if (!isNaN(idx)) selectImageIndex(idx);
        return;
      }

      // Image-carousel arrows (sit on the per-colour image carousel)
      if (e.target.closest('#pf-pp-image-carousel-prev')) {
        e.preventDefault();
        navigateImageArrow(-1);
        return;
      }
      if (e.target.closest('#pf-pp-image-carousel-next')) {
        e.preventDefault();
        navigateImageArrow(1);
        return;
      }

      // Main product image arrows — now scroll through the per-colour image set
      if (e.target.closest('#pf-pp-img-prev')) {
        e.preventDefault();
        navigateImageArrow(-1);
        return;
      }
      if (e.target.closest('#pf-pp-img-next')) {
        e.preventDefault();
        navigateImageArrow(1);
        return;
      }

      // Add to Basket button
      if (e.target.closest('#pf-pp-atc-btn')) {
        e.preventDefault();
        addToCart();
        return;
      }

      // Add and Checkout button
      if (e.target.closest('#pf-pp-checkout-btn')) {
        e.preventDefault();
        addToCartAndCheckout();
        return;
      }

      // Main product image click → fullscreen preview
      if (e.target.closest('#pf-pp-main-image')) {
        e.preventDefault();
        openImagePreview();
        return;
      }

      // Preview close button
      if (e.target.closest('#pf-pp-preview-close')) {
        e.preventDefault();
        closeImagePreview();
        return;
      }

      // Zoom in
      if (e.target.closest('#pf-pp-zoom-in')) {
        e.preventDefault();
        zoomPreview(1);
        return;
      }

      // Zoom out
      if (e.target.closest('#pf-pp-zoom-out')) {
        e.preventDefault();
        zoomPreview(-1);
        return;
      }

      // Click overlay background to close
      if (e.target.id === 'pf-pp-preview-overlay' || e.target.id === 'pf-pp-preview-img-wrap') {
        e.preventDefault();
        closeImagePreview();
        return;
      }
    });

    // Escape key closes preview
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var overlay = document.getElementById('pf-pp-preview-overlay');
        if (overlay && !overlay.hidden) {
          closeImagePreview();
        }
      }
    });
  }

  function updateSelectorUI(dimension, value) {
    var fieldset = state.sectionEl.querySelector('[data-selector="' + dimension + '"]');
    if (!fieldset) return;

    var buttons = fieldset.querySelectorAll('.pf-selector__option');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-checked', buttons[i].dataset.value === value ? 'true' : 'false');
    }
  }

  /* =========================================================================
     11. ADD TO CART
     ========================================================================= */

  function addToCart() {
    var variant = state.currentVariant;
    if (!variant || !variant.available) return;

    var atcBtn = document.getElementById('pf-pp-atc-btn');
    if (!atcBtn) return;

    // Loading state
    atcBtn.disabled = true;
    atcBtn.setAttribute('data-loading', 'true');

    // Build body
    var body = {
      items: [{
        id: variant.id,
        quantity: 1,
        properties: {}
      }]
    };

    // Goodwill message
    var goodwillEl = document.getElementById('pf-pp-goodwill');
    if (goodwillEl && goodwillEl.value.trim()) {
      body.items[0].properties['Goodwill Message'] = goodwillEl.value.trim();
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
          return data;
        });
      })
      .then(function (cartItem) {
        restoreAtcBtn(true);
        openCartDrawer();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cartItem: cartItem } }));
      })
      .catch(function (err) {
        restoreAtcBtn(false);
        console.error('[PFT] ATC error:', err);
      });

    function restoreAtcBtn(success) {
      atcBtn.disabled = false;
      atcBtn.removeAttribute('data-loading');
      if (success) {
        var textEl = atcBtn.querySelector('.pf-pp__atc-text');
        if (textEl) {
          textEl.textContent = 'Added!';
          setTimeout(function () { textEl.textContent = 'Add to Basket'; }, 2000);
        }
      }
    }
  }

  function openCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cartDrawer) {
      try { window.Shopify.theme.cartDrawer.open(); return; } catch (e) { /* noop */ }
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  /* =========================================================================
     12. IMAGE & CAROUSEL ARROWS
     ========================================================================= */

  /** Get the current product's per-colour image list (with featuredImage fallback). */
  function getCurrentImages() {
    var p = state.currentProduct;
    if (!p) return [];
    if (p.images && p.images.length > 0) return p.images;
    return p.featuredImage ? [p.featuredImage] : [];
  }

  /** Set the <img> at #pf-pp-main-image to the given Shopify CDN URL with srcset. */
  function setMainImageUrl(url, alt) {
    var imgEl = document.getElementById('pf-pp-main-image');
    if (!imgEl || !url) return;
    var baseUrl = url.replace(/&width=\d+/, '').replace(/\?width=\d+/, '');
    imgEl.src = baseUrl.indexOf('?') !== -1 ? baseUrl + '&width=900' : baseUrl + '?width=900';
    imgEl.srcset = [300, 600, 900, 1200].map(function (w) {
      return (baseUrl.indexOf('?') !== -1 ? baseUrl + '&width=' + w : baseUrl + '?width=' + w) + ' ' + w + 'w';
    }).join(', ');
    if (alt) imgEl.alt = alt;
  }

  /** Show the per-product description box only when this product has one. */
  function renderDescription(product) {
    var box = document.getElementById('pf-pp-description');
    var txt = document.getElementById('pf-pp-description-text');
    if (!box || !txt) return;
    var desc = (product && product.description ? String(product.description) : '').trim();
    if (desc) {
      txt.textContent = desc;
      box.hidden = false;
    } else {
      txt.textContent = '';
      box.hidden = true;
    }
  }

  /** Render the per-colour image carousel from product.images. */
  function renderImageCarousel(product) {
    var carousel = document.getElementById('pf-pp-image-carousel');
    var track = document.getElementById('pf-pp-image-track');
    if (!carousel || !track) return;

    var images = getCurrentImages();
    if (images.length <= 1) {
      // Hide the carousel when there's only one image (or none).
      carousel.hidden = true;
      track.innerHTML = '';
      return;
    }
    carousel.hidden = false;

    var html = '';
    for (var i = 0; i < images.length; i++) {
      var imgUrl = images[i].replace(/&width=\d+/, '').replace(/\?width=\d+/, '');
      var thumbUrl = imgUrl.indexOf('?') !== -1 ? imgUrl + '&width=120' : imgUrl + '?width=120';
      var isActive = i === state.currentImageIndex;
      html += '<button class="pf-pp__image-thumb' + (isActive ? ' pf-pp__image-thumb--active' : '') + '"' +
        ' data-image-index="' + i + '"' +
        ' aria-label="Image ' + (i + 1) + ' of ' + images.length + '"' +
        ' aria-current="' + (isActive ? 'true' : 'false') + '">' +
        '<img src="' + escapeAttr(thumbUrl) + '" alt="" loading="lazy">' +
        '</button>';
    }
    track.innerHTML = html;

    // Centre the active thumbnail
    setTimeout(function () {
      var activeThumb = track.querySelector('.pf-pp__image-thumb--active');
      if (!activeThumb) return;
      track.scrollLeft = activeThumb.offsetLeft - (track.offsetWidth / 2) + (activeThumb.offsetWidth / 2);
    }, 0);
  }

  /** Show/hide the main-image prev/next arrows based on per-colour image count. */
  function updateImageArrows() {
    var prevBtn = document.getElementById('pf-pp-img-prev');
    var nextBtn = document.getElementById('pf-pp-img-next');
    if (!prevBtn || !nextBtn) return;

    var images = getCurrentImages();
    var show = images.length > 1;
    prevBtn.hidden = !show;
    nextBtn.hidden = !show;
  }

  /** Navigate to prev/next image within the current colour. */
  function navigateImageArrow(direction) {
    var images = getCurrentImages();
    if (images.length <= 1) return;

    var newIdx = (state.currentImageIndex || 0) + direction;
    if (newIdx < 0) newIdx = images.length - 1;
    if (newIdx >= images.length) newIdx = 0;

    selectImageIndex(newIdx);
  }

  /** Set the active image (main + thumbnail highlight). */
  function selectImageIndex(idx) {
    var images = getCurrentImages();
    if (idx < 0 || idx >= images.length) return;
    state.currentImageIndex = idx;
    setMainImageUrl(images[idx], state.currentProduct ? state.currentProduct.title : '');

    // Update active thumb highlight
    var track = document.getElementById('pf-pp-image-track');
    if (track) {
      var thumbs = track.querySelectorAll('.pf-pp__image-thumb');
      for (var i = 0; i < thumbs.length; i++) {
        var active = i === idx;
        thumbs[i].classList.toggle('pf-pp__image-thumb--active', active);
        thumbs[i].setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          // Scroll the thumb into view inside the track
          var t = thumbs[i];
          track.scrollLeft = t.offsetLeft - (track.offsetWidth / 2) + (t.offsetWidth / 2);
        }
      }
    }
  }

  /** Init scroll arrows on the (existing) variant thumbnail carousel for COLOUR cycling. */
  function updateCarouselArrows() {
    var track = document.getElementById('pf-pp-variant-track');
    var prevBtn = document.getElementById('pf-pp-carousel-prev');
    var nextBtn = document.getElementById('pf-pp-carousel-next');
    if (!track || !prevBtn || !nextBtn) return;

    // Variant carousel still cycles colours for the current garment+cut+style+weight combo.
    var sel = state.selected;
    var colourProducts = colourVariantsFor(sel);
    var show = colourProducts.length > 1;
    prevBtn.style.display = show ? '' : 'none';
    nextBtn.style.display = show ? '' : 'none';

    prevBtn.onclick = function () { navigateColour(-1); };
    nextBtn.onclick = function () { navigateColour(1); };
  }

  /** Cycle through colour variants (used by the BELOW-image colour carousel arrows). */
  function navigateColour(direction) {
    var sel = state.selected;
    var colourProducts = colourVariantsFor(sel).sort(function (a, b) {
      return (a.dims.colour || '').localeCompare(b.dims.colour || '');
    });
    if (colourProducts.length <= 1) return;
    var currentIdx = -1;
    for (var i = 0; i < colourProducts.length; i++) {
      if (colourProducts[i].handle === state.currentProduct.handle) { currentIdx = i; break; }
    }
    if (currentIdx === -1) currentIdx = 0;
    var newIdx = currentIdx + direction;
    if (newIdx < 0) newIdx = colourProducts.length - 1;
    if (newIdx >= colourProducts.length) newIdx = 0;
    var newProduct = colourProducts[newIdx];
    state.selected.colour = newProduct.dims.colour;
    updateSelectorUI('colour', newProduct.dims.colour);
    cascadeFrom(SELECTOR_ORDER.indexOf('colour') + 1);
  }

  /* =========================================================================
     13. ARTWORK CAROUSEL — finite scroll (no duplicates)
     ========================================================================= */

  function initArtworkCarousel(fieldset) {
    var track = fieldset.querySelector('[data-track]');
    var leftArrow = fieldset.querySelector('.pf-selector__arrow--left');
    var rightArrow = fieldset.querySelector('.pf-selector__arrow--right');
    if (!track || !leftArrow || !rightArrow) return;

    artworkItemCount = track.children.length;

    track.style.transform = '';
    track.style.transition = '';

    if (artworkItemCount <= 1) {
      leftArrow.hidden = true;
      rightArrow.hidden = true;
      return;
    }

    centreArtworkCarousel(track);

    leftArrow.onclick = function () {
      var itemWidth = getArtworkItemWidth(track);
      track.scrollBy({ left: -itemWidth * 2, behavior: 'smooth' });
    };
    rightArrow.onclick = function () {
      var itemWidth = getArtworkItemWidth(track);
      track.scrollBy({ left: itemWidth * 2, behavior: 'smooth' });
    };

    if (!track.dataset.scrollBound) {
      track.addEventListener('scroll', updateArtworkArrows, { passive: true });
      track.dataset.scrollBound = '1';
    }

    updateArtworkArrows();
  }

  function updateArtworkArrows() {
    var fieldset = state.sectionEl ? state.sectionEl.querySelector('[data-selector="artwork"]') : null;
    if (!fieldset) return;
    var track = fieldset.querySelector('[data-track]');
    var leftArrow = fieldset.querySelector('.pf-selector__arrow--left');
    var rightArrow = fieldset.querySelector('.pf-selector__arrow--right');
    if (!track || !leftArrow || !rightArrow) return;

    var overflows = track.scrollWidth > track.clientWidth + 2;
    if (!overflows) {
      leftArrow.hidden = true;
      rightArrow.hidden = true;
      return;
    }

    leftArrow.hidden = track.scrollLeft <= 2;
    rightArrow.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  function getArtworkItemWidth(track) {
    var item = track.children[0];
    if (!item) return 100;
    var style = window.getComputedStyle(track);
    var gap = parseInt(style.gap) || 8;
    return item.offsetWidth + gap;
  }

  function centreArtworkCarousel(track) {
    if (!track) {
      var fieldset = state.sectionEl ? state.sectionEl.querySelector('[data-selector="artwork"]') : null;
      if (!fieldset) return;
      track = fieldset.querySelector('[data-track]');
    }
    if (!track) return;

    track.style.transform = '';
    track.style.transition = '';

    var items = track.children;
    if (items.length === 0) return;

    var selectedValue = state.selected.artwork;
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-value') === selectedValue) {
        var itemRect = items[i].getBoundingClientRect();
        var trackRect = track.getBoundingClientRect();
        if (itemRect.left < trackRect.left || itemRect.right > trackRect.right) {
          items[i].scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'auto' });
        }
        break;
      }
    }
    updateArtworkArrows();
  }

  /* =========================================================================
     14. SIZE GUIDE TABLE
     ========================================================================= */

  var sizeChartsData = null;

  function initSizeCharts() {
    var el = document.getElementById('pf-size-charts-data');
    if (el) {
      try { sizeChartsData = JSON.parse(el.textContent); } catch (e) {
        console.error('[PFT] Size charts parse error:', e);
      }
    }
  }

  function renderSizeGuide() {
    var container = document.getElementById('pf-pp-size-guide-table');
    var headerEl = document.getElementById('pf-pp-size-guide-header');
    if (!container || !headerEl || !sizeChartsData) return;

    var sel = state.selected;
    if (!sel.garment || !sel.cut || !sel.style || !sel.weight) {
      container.innerHTML = '';
      headerEl.textContent = '';
      return;
    }

    // Build lookup key
    var key = [sel.garment, sel.cut, sel.style, sel.weight].join('/');
    var chart = sizeChartsData.SIZE_CHARTS[key] || null;

    if (!chart) {
      container.innerHTML = '<p class="pf-pp__size-guide-empty">Size guide not available for this product.</p>';
      headerEl.textContent = 'SIZE GUIDE';
      return;
    }

    // Header: "SIZE GUIDE FOR SHORT SLEEVE | ADULT | T-SHIRT"
    headerEl.textContent = 'SIZE GUIDE FOR ' +
      titleCase(sel.style).toUpperCase() + ' \u007C ' +
      titleCase(sel.cut).toUpperCase() + ' \u007C ' +
      titleCase(sel.garment).toUpperCase();

    // Build table
    var measureKeys = Object.keys(chart.measurements);
    var html = '<table class="pf-pp__size-guide-table"><thead><tr><th>Size</th>';
    for (var m = 0; m < measureKeys.length; m++) {
      // Split "Half Chest (cm)" → first word + remainder, in spans so mobile CSS can stack
      // them onto two lines (item 7) while desktop keeps them inline on one line.
      var hk = measureKeys[m];
      var sp = hk.indexOf(' ');
      if (sp > 0) {
        html += '<th><span class="pf-sg-hw">' + escapeHtml(hk.slice(0, sp)) + '</span> <span class="pf-sg-hr">' + escapeHtml(hk.slice(sp + 1)) + '</span></th>';
      } else {
        html += '<th>' + escapeHtml(hk) + '</th>';
      }
    }
    html += '</tr></thead><tbody>';

    for (var s = 0; s < chart.sizes.length; s++) {
      html += '<tr><td>' + escapeHtml(chart.sizes[s]) + '</td>';
      for (var m2 = 0; m2 < measureKeys.length; m2++) {
        var val = chart.measurements[measureKeys[m2]][s];
        html += '<td>' + (val != null ? escapeHtml(String(val)) : '\u2014') + '</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    container.innerHTML = html;
  }

  /* =========================================================================
     15. ADD AND CHECKOUT
     ========================================================================= */

  function addToCartAndCheckout() {
    var variant = state.currentVariant;
    if (!variant || !variant.available) return;

    var btn = document.getElementById('pf-pp-checkout-btn');
    if (!btn) return;

    btn.disabled = true;
    btn.setAttribute('data-loading', 'true');

    var body = {
      items: [{
        id: variant.id,
        quantity: 1,
        properties: {}
      }]
    };

    var goodwillEl = document.getElementById('pf-pp-goodwill');
    if (goodwillEl && goodwillEl.value.trim()) {
      body.items[0].properties['Goodwill Message'] = goodwillEl.value.trim();
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
          return data;
        });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.removeAttribute('data-loading');
        console.error('[PFT] Checkout error:', err);
      });
  }

  /* =========================================================================
     16. FULLSCREEN IMAGE PREVIEW
     ========================================================================= */

  var previewZoomLevel = 1;
  var previewPanX = 0;
  var previewPanY = 0;
  var previewDragging = false;
  var previewDragStartX = 0;
  var previewDragStartY = 0;
  var previewPanStartX = 0;
  var previewPanStartY = 0;

  function applyPreviewTransform() {
    var previewImg = document.getElementById('pf-pp-preview-img');
    if (!previewImg) return;
    previewImg.style.transform = 'scale(' + previewZoomLevel + ') translate(' + previewPanX + 'px, ' + previewPanY + 'px)';
    previewImg.style.cursor = previewZoomLevel > 1 ? (previewDragging ? 'grabbing' : 'grab') : 'default';
  }

  function openImagePreview() {
    var mainImg = document.getElementById('pf-pp-main-image');
    var overlay = document.getElementById('pf-pp-preview-overlay');
    var previewImg = document.getElementById('pf-pp-preview-img');
    if (!mainImg || !overlay || !previewImg) return;

    // Move overlay to body so it escapes section stacking contexts
    if (overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
      initPreviewDrag(previewImg);
    }

    previewImg.src = mainImg.src;
    previewImg.alt = mainImg.alt;
    previewZoomLevel = 1;
    previewPanX = 0;
    previewPanY = 0;
    applyPreviewTransform();
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';

    // Hide Shopify header so it doesn't overlap the overlay
    var headerSection = document.querySelector('[id^="shopify-section"][id*="header"]');
    if (headerSection) headerSection.style.display = 'none';
  }

  function closeImagePreview() {
    var overlay = document.getElementById('pf-pp-preview-overlay');
    if (!overlay) return;
    overlay.hidden = true;
    document.body.style.overflow = '';
    previewZoomLevel = 1;
    previewPanX = 0;
    previewPanY = 0;
    previewDragging = false;

    // Restore header
    var headerSection = document.querySelector('[id^="shopify-section"][id*="header"]');
    if (headerSection) headerSection.style.display = '';
  }

  function zoomPreview(direction) {
    previewZoomLevel += direction * 0.5;
    if (previewZoomLevel < 0.5) previewZoomLevel = 0.5;
    if (previewZoomLevel > 4) previewZoomLevel = 4;

    // Reset pan when zooming back to 1x or below
    if (previewZoomLevel <= 1) {
      previewPanX = 0;
      previewPanY = 0;
    }
    applyPreviewTransform();
  }

  function initPreviewDrag(img) {
    // ── Desktop: mouse drag-to-pan ──
    function onMouseDown(e) {
      if (previewZoomLevel <= 1) return;
      e.preventDefault();
      previewDragging = true;
      previewDragStartX = e.clientX;
      previewDragStartY = e.clientY;
      previewPanStartX = previewPanX;
      previewPanStartY = previewPanY;
      applyPreviewTransform();
    }

    function onMouseMove(e) {
      if (!previewDragging) return;
      e.preventDefault();
      previewPanX = previewPanStartX + (e.clientX - previewDragStartX) / previewZoomLevel;
      previewPanY = previewPanStartY + (e.clientY - previewDragStartY) / previewZoomLevel;
      applyPreviewTransform();
    }

    function onMouseUp() {
      if (!previewDragging) return;
      previewDragging = false;
      applyPreviewTransform();
    }

    img.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // ── Mobile: pinch-to-zoom + one-finger pan ──
    var pinchStartDist = 0;
    var pinchStartZoom = 1;
    var isPinching = false;

    function getTouchDist(t1, t2) {
      var dx = t1.clientX - t2.clientX;
      var dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onTouchStart(e) {
      if (e.touches.length === 2) {
        // Pinch start
        e.preventDefault();
        isPinching = true;
        previewDragging = false;
        pinchStartDist = getTouchDist(e.touches[0], e.touches[1]);
        pinchStartZoom = previewZoomLevel;
      } else if (e.touches.length === 1) {
        // One-finger pan
        e.preventDefault();
        isPinching = false;
        previewDragging = true;
        previewDragStartX = e.touches[0].clientX;
        previewDragStartY = e.touches[0].clientY;
        previewPanStartX = previewPanX;
        previewPanStartY = previewPanY;
      }
    }

    function onTouchMove(e) {
      if (e.touches.length === 2 && isPinching) {
        // Pinch zoom
        e.preventDefault();
        var newDist = getTouchDist(e.touches[0], e.touches[1]);
        var scale = newDist / pinchStartDist;
        previewZoomLevel = Math.min(4, Math.max(0.5, pinchStartZoom * scale));
        if (previewZoomLevel <= 1) { previewPanX = 0; previewPanY = 0; }
        applyPreviewTransform();
      } else if (e.touches.length === 1 && previewDragging && !isPinching) {
        // One-finger pan (only when zoomed in)
        if (previewZoomLevel <= 1) return;
        e.preventDefault();
        previewPanX = previewPanStartX + (e.touches[0].clientX - previewDragStartX) / previewZoomLevel;
        previewPanY = previewPanStartY + (e.touches[0].clientY - previewDragStartY) / previewZoomLevel;
        applyPreviewTransform();
      }
    }

    function onTouchEnd(e) {
      if (e.touches.length < 2) isPinching = false;
      if (e.touches.length === 0) previewDragging = false;
    }

    img.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  /* =========================================================================
     17. UTILITIES
     ========================================================================= */

  function titleCase(slug) {
    if (!slug) return '';
    return slug.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function formatWeightLabel(slug) {
    var cased = titleCase(slug);
    return cased.replace(/^(\d+gsm)\s+(.+)$/, '$1 • $2');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

})();
